/** @file Export record of a mesh attachment: uvs/tris/hull plus rigid vertices (skin is added by runCompile). */
import type { ImageDoc, MeshAtt, Pose2DJson } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import type { Affine } from '@pose2d/math/types';
import { handleArray } from './handleArray';

type MeshJson = Extract<Pose2DJson['attachments'][number], { type: 'mesh' }>;

/** Mounted rigid vertices: each authored vertex mapped by the mount (final review C2/C7, ruling b). */
function mountedVertices(vertices: readonly number[], m: Affine): number[] {
  const out: number[] = [];
  const p: [number, number] = [0, 0];
  for (let i = 0; i + 1 < vertices.length; i += 2) {
    affine.apply(m, vertices[i] ?? 0, vertices[i + 1] ?? 0, p);
    out.push(p[0], p[1]);
  }
  return out;
}

/**
 * The rig record; `edges` and `mirrorVertexMap` are editor-only and dropped. A skinned mesh gets neither `vertices`
 * nor `skin` here: runCompile adds `skin` through the `skinning` stage (W2-18), so no stage calls another.
 */
export function meshRecord(att: MeshAtt, image: ImageDoc, mount: Affine | null): MeshJson {
  const g = att.geometry;
  const shape = g.kind === 'skinned' ? {} : { vertices: mount === null ? [...g.vertices] : mountedVertices(g.vertices, mount) };
  return {
    type: 'mesh', name: att.name, image: image.name, color: att.color,
    uvs: [...att.uvs], tris: [...att.triangles], hull: att.hull, ...shape,
    ...(att.handle === undefined ? {} : { handle: handleArray(att.handle) }),
    ...(att.mount === undefined ? {} : { fitLength: att.mount.fitLength }),
  };
}
